import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Switch,
  InputNumber,
  Card,
  Select,
  Row,
  Col,
  Space,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation } from "react-query";
import { useNavigate, useParams } from "react-router-dom";

import client from "../../api";
import routes from "src/config/routes";
import { logger } from "src/lib/utils";

const { Option } = Select;

interface ICardConditionForm {
  product_id: number;
  package_type: string;
  resident_fee: number;
  non_resident_fee: number;
  description: {
    title: string;
    subtitle: string;
    features: string[];
  };

  active: boolean;
}

const CardTermsConditionsForm = () => {
  const [form] = Form.useForm();

  const navigate = useNavigate();

  const { id } = useParams();

  const isEdit = !!id;

  const [products, setProducts] = useState<any[]>([]);

  // ===========================
  // PRODUCT LIST
  // ===========================

  const { mutate: getProducts } = useMutation(client.cardProducts.list, {
    onSuccess: (res: any) => {
      logger.log("Products", res);

      setProducts(res?.data?.data || []);
    },
  });

  // ===========================
  // ADD
  // ===========================

  const { mutate: addTerm, isLoading } = useMutation(
    client.cardTermsConditions.add,
    {
      onSuccess: (res: any) => {
        logger.log("Add Response", res);

        navigate(routes.cardTermsConditions);
      },
    },
  );

  // ===========================
  // UPDATE
  // ===========================

  const { mutate: updateTerm, isLoading: isUpdating } = useMutation(
    client.cardTermsConditions.update,
    {
      onSuccess: (res: any) => {
        logger.log("Update Response", res);

        navigate(routes.cardTermsConditions);
      },
    },
  );

  // ===========================
  // VIEW
  // ===========================

  const { mutate: getTerm, isLoading: isFetching } = useMutation(
    client.cardTermsConditions.view,
    {
      onSuccess: (res: any) => {
        logger.log("View Response", res);

        const data = res?.data || {};

        form.setFieldsValue({
          product_id: data.product?.id,

          package_type: data.package_type,

          resident_fee: Number(data.resident_fee),

          non_resident_fee: Number(data.non_resident_fee),

          description_title: data.description?.title,

          description_subtitle: data.description?.subtitle,

          description_features: data.description?.features?.length
            ? data.description.features
            : [""],
          active: data.active,
        });
      },
    },
  );

  useEffect(() => {
    getProducts({
      page: 1,
      limit: 100,
    });

    if (isEdit && id) {
      getTerm({ id });
    }
  }, []);

  // ===========================
  // SUBMIT
  // ===========================

  const onFinish = (values: any) => {
    const payload: ICardConditionForm = {
      product_id: values.product_id,
      package_type: values.package_type,
      resident_fee: values.resident_fee,
      non_resident_fee: values.non_resident_fee,
      description: {
        title: values.description_title,
        subtitle: values.description_subtitle,
        features: values.description_features,
      },
      active: values.active,
    };

    if (isEdit) {
      updateTerm({
        id,
        ...payload,
      });

      return;
    }

    addTerm(payload);
  };

  return (
    <Card
      title={
        isEdit ? "Edit Card Terms & Conditions" : "Add Card Terms & Conditions"
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          active: true,
          //   residency_type: "RESIDENT",
          package_type: "MONTHLY",
          max_cards_per_product: 1,
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Product"
              name="product_id"
              rules={[
                {
                  required: true,
                  message: "Please select product",
                },
              ]}
            >
              <Select placeholder="Select Product">
                {products.map((item: any) => (
                  <Option key={item.id} value={item.id}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* <Form.Item
              label="Residency Type"
              name="residency_type"
              rules={[
                {
                  required: true,
                  message: "Please select residency type",
                },
              ]}
            >
              <Select>
                <Option value="RESIDENT">Resident</Option>
                <Option value="NON_RESIDENT">Non Resident</Option>
              </Select>
            </Form.Item> */}
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Package Type"
              name="package_type"
              rules={[
                {
                  required: true,
                  message: "Please select package",
                },
              ]}
            >
              <Select>
                <Option value="MONTHLY">Monthly</Option>
                <Option value="YEARLY">Yearly</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Resident Fee"
                  name="resident_fee"
                  rules={[
                    {
                      required: true,
                      message: "Please enter resident fee",
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Non Resident Fee"
                  name="non_resident_fee"
                  rules={[
                    {
                      required: true,
                      message: "Please enter non resident fee",
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
        <Form.Item
          label="Description Title"
          name="description_title"
          rules={[
            {
              required: true,
              message: "Please enter title",
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Description Subtitle"
          name="description_subtitle"
          rules={[
            {
              required: true,
              message: "Please enter subtitle",
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.List name="description_features">
          {(fields, { add, remove }) => (
            <>
              <label className="font-medium mb-3 block">Features</label>

              {fields.map(({ key, name }) => (
                <Space
                  key={key}
                  align="baseline"
                  style={{
                    display: "flex",
                    marginBottom: 8,
                  }}
                >
                  <Form.Item
                    name={name}
                    rules={[
                      {
                        required: true,
                        message: "Enter feature",
                      },
                    ]}
                  >
                    <Input placeholder="Feature" style={{ width: 500 }} />
                  </Form.Item>

                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
              ))}

              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => add()}
              >
                Add Feature
              </Button>
            </>
          )}
        </Form.List>

        {/* <Form.Item
          label="Card Condition URL"
          name="card_condition_url"
          rules={[
            {
              required: true,
              message: "Please enter card condition URL",
            },
            {
              type: "url",
              message: "Enter a valid URL",
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Terms & Conditions URL"
          name="terms_and_conditions_url"
          rules={[
            {
              required: true,
              message: "Please enter terms URL",
            },
            {
              type: "url",
              message: "Enter a valid URL",
            },
          ]}
        >
          <Input />
        </Form.Item> */}

        <Form.Item label="Active" name="active" valuePropName="checked">
          <Switch />
        </Form.Item>

        <div className="flex justify-end gap-3 mt-6">
          <Button onClick={() => navigate(routes.cardTermsConditions)}>
            Cancel
          </Button>

          <Button
            className="bg-green-600 hover:bg-green-700 text-white border-none"
            type="primary"
            htmlType="submit"
            loading={isLoading || isUpdating || isFetching}
          >
            {isEdit ? "Update" : "Save"}
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default CardTermsConditionsForm;
